import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Alert, Badge, Breadcrumb, Button, Modal,MultiSelect, Search, StatusHint, Tag, Textarea, Checkbox, TextLink, Tooltip } from '@indipia/ui-core';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAppStore, useAuthStore } from '@indipia/shared';
import { PartnerFormPopup, SiteRegistrationPopup, StepD, type StepBResult, assigneeSummaryText, useMineOnlyState } from '@indipia/shared-features';
import { worklogApi } from '../api/worklogApi';
import { applyMineOnly, matchesSiteQuery } from '../utils/siteAssignees';
import { ganttPartnerAssignPath, partnerStatusView, type PartnerStatusView } from '../utils/partnerStatus';
import { useWorklogStore } from '../store/worklogStore';
import { Toast } from '../components/Toast';
import { StatusChip } from '../components/StatusChip';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { ThumbImg } from '../components/ThumbImg';
import { IndiPhotoPickerModal, type PickedIndiPhoto } from '../components/IndiPhotoPickerModal';
import { WorkOrderNoticeView } from '../components/WorkOrderNoticeView';
import { WorkOrderTabs } from '../components/WorkOrderTabs';
import { Temp_WorkerSelect, type WorkerSelectOption } from '../components/Temp_WorkerSelect';
import { useWorkTypeName } from '../hooks/useWorkTypeName';
import {
  INDI_PHOTO_AGENT_ID,
  KeywordChips,
  PhotoAttach,
  PhotoPreview,
  revokePreview,
  type LocalPhoto,
} from '../components/OrderAttachments';
import { PHOTO_POLICY, pickValidPhotos, photoDropNotice } from '../config/uploadPolicy';
import { CUST_REQ_KEY, custReqErrorMessage, useCustReqList } from '@indipia/shared-features';
import { CustReqDeliveryCard, type CustReqLoadStatus } from '../components/custReq/CustReqDeliveryCard';
import {
  CUST_REQ_ORDER_MESSAGE,
  applyMemoOnTaskChange,
  canReloadMemo,
  canSendSingle,
  deliveredNoticeText,
  includedOf,
  isCustReqConflict,
  memoHintKind,
  memoHintText,
  receivedOnly,
  toRefs,
  toggleExcluded,
} from '../utils/custReqOrder.logic';
import type {
  BasicInfoPartner,
  BasicInfoWorklog,
  SiteOption,
  WorkOrderCreateResult,
  WorkOrderPhotoMeta,
  WorkOrderSiteInfo,
  WorkOrderSiteWorkType,
} from '../types/worklog.types';

/**
 * 작업요청 등록 화면 (기능명세 B2 / API §2.3).
 *
 * - 현장: worklogStore(basic-info)의 시공중·준비중 현장에서 선택(재조회 없음)
 * - 수신자: worklogStore.partners에서 다중 선택(최소 1명)
 * - 내키워드: 조회/삽입/추가/삭제/순서변경 (API §2.7~2.10)
 * - 사진첨부: 로컬 미리보기 UI만 (sv-photo 업로드 미연동 → 전송 시 photos 생략)
 * - 발송: POST /worklogs/{worklogId}/work-orders
 */

const BASE = '/agents/work-log';
// 사진 첨부 정책은 uploadPolicy(PHOTO_POLICY) SSOT + pickValidPhotos/photoDropNotice 공통 함수 사용.
// LocalPhoto·PhotoPreview·KeywordChips·PhotoAttach는 일괄 작성(WorkOrderBulkPage)과 공유하는 공용 부품이라
// components/OrderAttachments로 뺐다(§4.1.3·§4.1.4 — 두 화면이 같은 UI/동작).

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
function todayParts(): { date: string; ymd: string; weekday: string; dow: number } {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}`,
    /** yyyyMMdd — site-info workDate 파라미터용(§6.6) */
    ymd: `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`,
    weekday: WEEKDAYS[d.getDay()],
    dow: d.getDay(),
  };
}

const WORK_LOG_AGENT_ID = 2; // 인디공사현황 numericAgentId (현장등록 팝업용)

/** 주소 파라미터(`?siteId=&taskId=`) 값 — 양의 정수가 아니면 무시한다. */
function parsePositiveInt(raw: string | null): number | null {
  return raw && /^\d+$/.test(raw) && Number(raw) > 0 ? Number(raw) : null;
}

/** "요청자" 표기 — BE WorkOrderShareService#buildRequestedByLabel과 동일 규칙(미리보기 REQ-1-11 근사치). */
function buildRequestedByLabel(repName: string | null | undefined, companyName: string | null | undefined): string {
  const rep = repName?.trim();
  const comp = companyName?.trim();
  if (rep && comp) return `${rep} (${comp})`;
  return rep || comp || '';
}

// ─────────────────────────────────────────────────────────────────────────
// 공사기간 입력 모달 — site만 있는 현장(worklogId=null) 선택 시 Step D(공사기간)만 단독으로 띄운다.
// 전체 현장등록 마법사(SiteRegistrationPopup) 없이 shared-features의 StepD를 그대로 재사용.
// ─────────────────────────────────────────────────────────────────────────
function ConstructionPeriodModal({
  siteId,
  siteName,
  onClose,
  onComplete,
}: {
  siteId: number;
  siteName: string | null;
  onClose: () => void;
  onComplete: (siteId: number) => void;
}) {
  // StepD는 siteId로 fetchSite()를 다시 조회해 실제 주소·동·호를 채우므로, 나머지 필드는 빈 값으로 넘겨도 된다.
  const stepBResult: StepBResult = {
    siteId,
    bldgId: null,
    roadAddress: '',
    sitePyeong: null,
    squareMeters: null,
    bldgName: null,
    addressDong: null,
    addressHo: null,
  };
  return (
    <Modal open onClose={onClose} title="공사기간을 지정해주세요." size="lg">
      {/* 갑자기 뜨는 팝업이라 왜 뜨는지·무엇을 해야 하는지 먼저 안내한다 */}
      <Alert
        kind="info"
        className="mb-3"
        icon={
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-info)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        }
      >
        <p className="text-body-sm leading-relaxed">
          <b className="font-bold">{siteName || '선택하신 현장'}</b>은(는) 공사현황에서 처음 사용하는
          현장이에요. <br />공사 <b>시작일과 종료일을 선택하고 저장</b>하면, 바로 작업요청을 작성할 수 있어요.
        </p>
      </Alert>
      <StepD
        stepBResult={stepBResult}
        onComplete={(id) => onComplete(id)}
        onClose={onClose}
      />
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 현장 선택 드롭다운 (worklogStore.siteOptions — BE가 이미 정렬·필터링해 내려줌)
// ─────────────────────────────────────────────────────────────────────────
function SiteSelect({
  siteOptions,
  status,
  selected,
  onSelectWorklog,
  onSelectSiteOnly,
}: {
  siteOptions: SiteOption[];
  status: string;
  selected: BasicInfoWorklog | null;
  onSelectWorklog: (w: BasicInfoWorklog) => void;
  onSelectSiteOnly: (opt: SiteOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [regOpen, setRegOpen] = useState(false);

  // '☑ 내 현장만'(2026-10-12 개선사항 D) — 직원은 기본 체크, 사장님은 기본 해제. 해제하면 모든 진행중 현장 선택 가능.
  // 목록은 내 현장 먼저(그룹 없음), 검색은 현장명 · 고객명 · 담당자·관리자 이름.
  const [mineOnly, setMineOnly] = useMineOnlyState();
  const list = applyMineOnly(siteOptions, mineOnly).filter((o) => matchesSiteQuery(o, q));

  // 새 현장 등록 완료 → 공사현황 생성 → basic-info 갱신 → 새 현장 자동 선택
  async function handleRegistered(siteId: number) {
    setRegOpen(false);
    try {
      // 공사현황 생성은 등록 팝업 Step D(공사기간)가 담당 — 여기서 만들면 중복 생성된다.
      await useWorklogStore.getState().refresh();
      const w = useWorklogStore.getState().worklogs.find((x) => x.siteId === siteId);
      if (w) onSelectWorklog(w);
    } catch {
      alert('현장 등록에 실패했습니다. 다시 시도해주세요.');
    }
  }

  return (
    <>
    <div className={`field${open ? ' combo open' : ' combo'}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input flex items-center gap-2"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="shrink-0 text-text-muted">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        <span className={selected ? 'flex-1 truncate text-left font-medium' : 'flex-1 truncate text-left text-text-muted'}>
          {selected ? selected.siteName ?? '(현장명 없음)' : '현장을 선택하세요'}
        </span>
        <span className="caret">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-dropdown" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute left-0 top-full z-dropdown mt-1 w-[320px] overflow-hidden rounded-lg border border-border bg-surface-raised shadow-lg">
            <div className="border-b border-border p-2">
              <Search autoFocus value={q} onChange={setQ} placeholder="현장명, 고객명, 담당자 검색" />
            </div>
            <div className="max-h-64 overflow-auto">
              {status === 'loading' && (
                <div className="py-6 text-center text-body-sm text-text-muted">불러오는 중…</div>
              )}
              {status === 'error' && (
                <div className="py-6 text-center text-body-sm text-text-muted">현장 목록을 불러오지 못했습니다.</div>
              )}
              {status === 'ready' && list.length === 0 && (
                <div className="py-06 text-center text-body-sm text-text-muted">{mineOnly && !q ? '내가 담당·관리하는 진행중 현장이 없습니다' : '검색 결과 없음'}</div>
              )}
              {list.map((o) => (
                <button
                  key={`${o.worklogId ?? 'site'}-${o.siteId}`}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setQ('');
                    if (o.worklogId != null) {
                      const w = useWorklogStore.getState().worklogs.find((x) => x.worklogId === o.worklogId);
                      if (w) onSelectWorklog(w);
                    } else {
                      onSelectSiteOnly(o);
                    }
                  }}
                  className="flex w-full items-center gap-2 border-b border-border px-3 py-2.5 text-left last:border-b-0 hover:bg-surface-muted"
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-body-sm font-semibold text-text">{o.siteName ?? '(현장명 없음)'}</span>
                    {/* 둘째 줄: 고객명 + 담당 ○○ 외 N · 관리 ○○(지정 순서 첫 번째, 미지정이면 '담당자 미지정') — 회색 캡션, 강조 없음 */}
                    <span className="flex min-w-0 items-center gap-md text-caption">
                      <span className="truncate text-text-secondary">{o.customerName ?? '-'}</span>
                      <span className="truncate text-text-muted">
                        {assigneeSummaryText(o.chargeMembers, o.managerMembers)}
                      </span>
                    </span>
                  </span>
                  {o.progressStatus ? (
                    <StatusChip code={o.progressStatus} />
                  ) : (
                    <Tag className="shrink-0 !text-[10px] !font-bold">
                      {o.siteStatusName}
                    </Tag>
                  )}
                </button>
              ))}
            </div>
            {/* + 새 현장 추가하기 */}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setRegOpen(true);
              }}
              className="flex w-full items-center gap-2 border-t border-dashed border-group/40 bg-group-subtle px-3 py-3 text-body-sm font-bold text-group hover:bg-group-subtle/70"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              공사현장 추가하기
            </button>
          </div>
        </>
      )}

      {regOpen && (
        <SiteRegistrationPopup
          agentId={WORK_LOG_AGENT_ID}
          onClose={() => setRegOpen(false)}
          onComplete={(siteId) => handleRegistered(siteId)}
        />
      )}
    </div>
    {/* ☑ 내 현장만 — 현장 셀렉박스 오른쪽(D) */}
    <Tooltip tip="내가 현장 담당자 또는 현장 관리자인 현장만 보기" tabIndex={-1} className="shrink-0">
      <Checkbox
        label="내 현장만"
        checked={mineOnly}
        onChange={(e) => setMineOnly(e.target.checked)}
      />
    </Tooltip>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 작업공종 선택 드롭다운 — 화면기획서 실측(.gongsel-*): 목록 상단 안내문구 + (스케줄 현장) 항목 오른쪽에 담당 시공반장.
// ui-core Combobox 는 옵션이 value/label 뿐이라 머리글·보조 텍스트를 담을 수 없다 — 공통 패키지는 건드리지 않고
// 현장 선택(SiteSelect)과 같은 방식으로 이 페이지에 직접 둔다. 표준 CSS(.field/.combo/.input/.menu/.menu-item)는 그대로 재사용하고,
// 공통 Combobox 가 하던 키보드 조작(↑↓·Enter·Esc)과 바깥 클릭 닫기는 유지한다.
// ─────────────────────────────────────────────────────────────────────────
/** 목록 상단 안내문구 — 공종 목록이 어디서 왔는지(스케줄 / 오늘 이후 공정 없음 / 스케줄 없음) */
const WORK_TYPE_HEADER_SCHEDULE = '이 현장의 공사스케줄에서 선택';
const WORK_TYPE_HEADER_NO_TODAY = '오늘 이후 진행할 공정이 없어 공종마스터에서 선택';
const WORK_TYPE_HEADER_NO_SCHEDULE = '등록된 공사스케줄이 없어 공종마스터에서 선택';
/** 트리거(=목록) 폭 기준 — 가장 긴 안내문구가 한 줄로 들어가는 너비. 현장을 바꿔도 폭이 들썩이지 않게 항상 이 문구로 잡는다. */
const WORK_TYPE_HEADER_LONGEST = [WORK_TYPE_HEADER_SCHEDULE, WORK_TYPE_HEADER_NO_TODAY, WORK_TYPE_HEADER_NO_SCHEDULE].reduce(
  (a, b) => (b.length > a.length ? b : a),
);

interface WorkTypeSelectOption {
  value: string;
  label: string;
  /**
   * 스케줄 공정의 담당 시공반장 — string 이면 그 이름, null 이면 이름 없이 '미배정' 배지만.
   * undefined(공종마스터 목록)는 오른쪽 표기를 하지 않는다.
   */
  partnerName?: string | null;
  /** 반장 일정 확정 여부 태그(REQ-11-01) — null/undefined 면 태그 없음. 확정이 아니면 이름을 옅게 보여 준다(선택은 가능). */
  status?: PartnerStatusView | null;
}

type WorkTypeRowProps = Pick<WorkTypeSelectOption, 'label' | 'partnerName' | 'status'>;

/** 목록 한 행의 내용 — 실제 행과 폭 기준(보이지 않는 복제)이 같은 모양을 쓰도록 한 곳에 둔다. */
function WorkTypeRowContent({ label, partnerName, status }: WorkTypeRowProps) {
  return (
    <>
      {/* 확정이 아닌 공종은 공종명만 옅게 — 목업(.gongsel-item.dim)도 첫 칸만 흐리고 반장·태그는 그대로 둔다 */}
      <span className={`min-w-0 truncate${status && !status.confirmed ? ' text-text-muted' : ''}`}>{label}</span>
      {partnerName !== undefined && (
        <span className="flex shrink-0 items-center gap-sm">
          {/* 목업은 미매칭을 힌트색(text-disabled)으로 더 옅게 그리지만 다크 모드에서 AA 명암비(4.5:1)에 못 미쳐(3.39:1) 같은 색으로 둔다 — 문구가 뜻을 전한다 */}
          {/* 반장이 없으면 이름 자리를 비우고 '미배정' 배지만 보인다(같은 뜻 중복 표시 방지) */}
          {partnerName && <span className="text-caption font-normal text-text-muted">{partnerName}</span>}
          {status && <Badge status={status.status}>{status.label}</Badge>}
        </span>
      )}
    </>
  );
}

/**
 * 폭 기준 행 — 가장 긴 두 조합(공종명 + 반장 + 기한이 붙은 '회신 대기' 태그 / '미배정' 태그)이
 * 한 줄에 들어가는 너비로 트리거(=목록) 폭을 잡는다. 현장이 바뀌어도 폭이 들썩이지 않게 항상 이 둘로 잡는다.
 */
const WORK_TYPE_ROW_SIZERS: WorkTypeRowProps[] = [
  { label: '전기(배선)', partnerName: '김반장님', status: { label: '회신 대기 · 기한 00.00 00:00', status: 'warning', confirmed: false } },
  { label: '전기(배선)', partnerName: null, status: { label: '미배정', status: 'mute', confirmed: false } },
];

function WorkTypeSelect({
  value,
  onChange,
  options,
  header,
  disabled,
  placeholder,
  emptyText,
}: {
  value: string;
  onChange: (value: string) => void;
  options: WorkTypeSelectOption[];
  header: string;
  disabled?: boolean;
  placeholder: string;
  emptyText: string;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const closeMenu = () => {
    setOpen(false);
    setActiveIndex(-1);
  };
  const select = (opt: WorkTypeSelectOption) => {
    onChange(opt.value);
    closeMenu();
  };

  // 바깥 클릭 닫기 — 오버레이를 깔지 않고 문서 mousedown 으로 처리(오버레이 z-index 충돌 방지)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // 현장 변경 등으로 비활성이 되면 열려 있던 목록을 닫는다
  useEffect(() => {
    if (disabled) closeMenu();
  }, [disabled]);

  // 열 때 선택된 항목이 목록 안쪽에 있으면 보이는 위치로 옮긴다 — 목록(menuRef)만 스크롤해 페이지는 흔들지 않는다
  useEffect(() => {
    if (open && menuRef.current && selectedRef.current) {
      const menu = menuRef.current;
      const item = selectedRef.current;
      menu.scrollTop = item.offsetTop - menu.clientHeight / 2 + item.offsetHeight / 2;
    }
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open && activeIndex >= 0 && options[activeIndex]) {
      e.preventDefault();
      select(options[activeIndex]);
    } else if (e.key === 'Escape' && open) {
      e.stopPropagation();
      closeMenu();
    }
  };

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div ref={rootRef} className={`field field-auto combo${open ? ' open' : ''}`}>
      <button
        type="button"
        className="input flex items-center"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        {/* 폭 기준 — 보이지 않는 안내문구(가장 긴 것)와 가장 긴 행으로 트리거 폭을 잡아 목록 폭(.combo .menu = 100%)이 그 내용에 맞게 한다(고정 px 금지, D-07 §3.2) */}
        <span className="grid min-w-0 flex-1 text-left">
          <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-nowrap text-caption font-bold">
            {WORK_TYPE_HEADER_LONGEST}
          </span>
          {WORK_TYPE_ROW_SIZERS.map((row, i) => (
            <span key={i} aria-hidden className="invisible col-start-1 row-start-1 flex items-center justify-between gap-md whitespace-nowrap">
              <WorkTypeRowContent {...row} />
            </span>
          ))}
          <span className={`col-start-1 row-start-1 min-w-0 truncate ${selectedLabel ? 'font-medium' : 'text-text-muted'}`}>
            {selectedLabel || placeholder}
          </span>
        </span>
        <span className="caret" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="menu show" role="listbox" id={listId} ref={menuRef}>
          <div className="mb-02 whitespace-nowrap rounded-md bg-surface-muted px-03 py-02 text-caption font-bold text-text-muted">
            {header}
          </div>
          {options.length === 0 && <div className="combo-empty">{emptyText}</div>}
          {options.map((opt, i) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={`${opt.value}-${i}`}
                ref={isSelected ? selectedRef : undefined}
                role="option"
                aria-selected={isSelected}
                className={`menu-item justify-between gap-md${isSelected ? ' selected' : ''}${i === activeIndex ? ' active' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(opt)}
                onMouseEnter={() => setActiveIndex(i)}
              >
                <WorkTypeRowContent label={opt.label} partnerName={opt.partnerName} status={opt.status} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 메인 페이지
// ─────────────────────────────────────────────────────────────────────────
export function WorkOrderCreatePage() {
  const navigate = useNavigate();
  const siteOptions = useWorklogStore((s) => s.siteOptions);
  const partners = useWorklogStore((s) => s.partners);
  /** 공종 코드 → 공종명(작업자 목록 둘째 줄 전문분야 표시용) */
  const workTypeNameOf = useWorkTypeName();
  const storeStatus = useWorklogStore((s) => s.status);
  const todayIsHoliday = useWorklogStore((s) => s.todayIsHoliday);
  const holidayName = useWorklogStore((s) => s.holidayName);
  const memberId = useAuthStore((s) => s.user?.memberId ?? null);
  const session = useAppStore((s) => s.session);

  /**
   * 화면 진입 시 basic-info 강제 재조회 — worklog.scheduleId 를 sv-schedule 최신 상태로 맞춘다.
   *
   * worklogStore 는 에이전트 진입 시 1회만 로드되고 이후 세션 내내 캐시된다(WorkLogLayout).
   * 이 화면은 현장 선택 즉시 그 시점 DB의 scheduleId 로 스케줄 상세를 조회하므로(§2.12 site-info),
   * 세션이 길어질수록 "basic-info 마지막 동기화 이후 스케줄 변경분"을 놓칠 여지가 커진다.
   * 매 렌더가 아니라 **이 화면에 들어올 때마다**(진입 시 1회) 갱신해 그 시차를 좁힌다.
   * status 는 SiteSelect 가 loading/error/ready 를 이미 표시하므로 별도 로딩 UI가 필요 없다.
   */
  useEffect(() => {
    void useWorklogStore.getState().refresh();
  }, []);

  const today = todayParts();
  const weekdayColor =
    todayIsHoliday || today.dow === 0
      ? 'var(--color-error)'
      : today.dow === 6
        ? 'var(--color-info)'
        : 'var(--shell-on-surface)';

  const [selected, setSelected] = useState<BasicInfoWorklog | null>(null);

  // ── 다른 화면(오늘 할일 · 나의 할일 · 홈 배너 등)에서 현장 · 공정을 지정해 연 경우: `?siteId=&taskId=` (REQ-11-03, X-04) ──
  // 모르는 값 · 열 수 없는 현장(진행중 목록에 없음)은 무시하고 평소처럼 연다. 현장 · 공정 미리 선택은 각각 한 번만 적용한다
  // (사용자가 현장을 바꾸거나 해제한 뒤 주소 값이 다시 끼어들지 않게).
  const [searchParams] = useSearchParams();
  const linkSiteId = parsePositiveInt(searchParams.get('siteId'));
  const linkTaskId = linkSiteId != null ? parsePositiveInt(searchParams.get('taskId')) : null;
  const worklogs = useWorklogStore((s) => s.worklogs);
  const linkSiteApplied = useRef(false);
  const linkTaskApplied = useRef(false);
  useEffect(() => {
    if (linkSiteApplied.current || linkSiteId == null) return;
    if (selected != null) {
      linkSiteApplied.current = true; // 사용자가 먼저 현장을 골랐으면 주소는 따르지 않는다
      return;
    }
    const w = worklogs.find((x) => x.siteId === linkSiteId);
    if (!w) return; // 기준정보가 아직 안 왔을 수 있다 — 도착하면 다시 시도
    linkSiteApplied.current = true;
    setSelected(w);
  }, [linkSiteId, worklogs, selected]);

  /** 작업공종 1개(REQ-1-02) — case1(스케줄 있음)·case2(없음)는 workTypeOptions가 결정 */
  const [workTypeCode, setWorkTypeCode] = useState('');
  /** 스케줄 공정 논리참조(REQ-1-03) — case1·2에서 공정을 고른 경우만. 전체 공종마스터(case2)에서 고르면 null */
  const [taskId, setTaskId] = useState<number | null>(null);
  /** 작업자 1명(필수, REQ-1-04) — 공종 선택에 종속(case A 자동배정 / case B 직접지정) */
  const [worker, setWorker] = useState<BasicInfoPartner | null>(null);
  /** 참조인 여러 명(선택, REQ-1-06) */
  const [ccList, setCcList] = useState<BasicInfoPartner[]>([]);
  /** "새 시공반장 등록" 팝업(공용 PartnerFormPopup) — 어느 쪽에서 열었는지로 저장 후 배정 대상을 가른다 */
  const [addPartnerFor, setAddPartnerFor] = useState<'worker' | 'cc' | null>(null);
  const [content, setContent] = useState('');
  // ── 고객 요청(S16, R-28·R-29) — 선택 공정의 접수 상태 요청을 함께 전달(기본 켜짐, 해제=이번에 빼기). 공정이 없으면 조회·표시하지 않는다(확정결정 25) ──
  const qc = useQueryClient();
  const [excludedReqs, setExcludedReqs] = useState<Set<number>>(() => new Set());
  const custReqQuery = useCustReqList(taskId);
  const receivedReqs = useMemo(() => receivedOnly(custReqQuery.data), [custReqQuery.data]);
  const includedReqs = useMemo(() => includedOf(receivedReqs, excludedReqs), [receivedReqs, excludedReqs]);
  const includedKey = includedReqs.map((r) => `${r.custReqId}:${r.updatedAt}`).join(',');
  /** 요청 목록을 확인하는 중이거나 실패했으면 발송을 막는다(요청을 빠뜨린 채 보내지 않음) */
  const reqBlocked = taskId != null && (custReqQuery.isFetching || custReqQuery.isError);
  const reqStatus: CustReqLoadStatus = custReqQuery.isError ? 'error' : custReqQuery.isFetching && !custReqQuery.data ? 'loading' : 'ready';
  /** 작업 요청내용에 마지막으로 자동 채운 메모 — '불러온 그대로' 판정 기준 */
  const loadedMemoRef = useRef<string | null>(null);
  const contentRef = useRef(content);
  contentRef.current = content;
  const [photos, setPhotos] = useState<LocalPhoto[]>([]);
  // 인디포토 사진첩 선택 팝업 + 인디포토 구독 여부(미구독이면 조회 없이 안내만)
  const [indiPhotoOpen, setIndiPhotoOpen] = useState(false);
  const indiPhotoSubscribed = useAppStore((s) =>
    s.subscriptions.some((sub) => sub.agentId === INDI_PHOTO_AGENT_ID),
  );
  // 사진 첨부 제외 안내 토스트(개수·용량 초과) — D-09 §15.8
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const [pendingSite, setPendingSite] = useState<BasicInfoWorklog | null>(null);
  // site만 있는 현장(worklogId=null) 선택 시 schedule-lookup 조회 중 — 자동생성/팝업 분기 대기
  const [scheduleLookupLoading, setScheduleLookupLoading] = useState(false);
  // schedule-lookup 결과 공사기간이 불완전해 공사기간 입력 팝업(Step D)을 띄워야 하는 현장
  const [periodPopupSite, setPeriodPopupSite] = useState<SiteOption | null>(null);
  const [lightbox, setLightbox] = useState<number | null>(null);
  /** 알림톡 미리보기 모달(REQ-1-11) — [카카오톡 발송]과 별개 진입점, 모달 안 [발송하기]도 같은 send()를 호출 */
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<WorkOrderCreateResult | null>(null);
  /**
   * 발송 완료 안내문구용 수신자명 — 등록 응답(§2.3 sendResults) 순서대로 '홍반장, 정전기' 형태로 나열한다.
   * 이름을 못 찾은 수신자는 문구에서 제외한다(내부 식별자를 사장님 화면에 노출하지 않는다).
   */
  const allRecipients = useMemo(() => (worker ? [worker, ...ccList] : ccList), [worker, ccList]);
  /**
   * 생성 중복방지키(REQ-1-10) — 발송 폼 입력값(현장·공종·작업자·참조인·내용·사진) 중 하나라도 바뀌면
   * 재발급한다(§5.2). 값이 그대로면 같은 키를 유지해, 같은 내용을 실수로 두 번 저장하지 않게 한다.
   */
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const ccKey = ccList.map((p) => p.partnerId).join(',');
  const photoKey = photos.map((p) => p.id).join(',');
  useEffect(() => {
    setRequestKey(crypto.randomUUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.worklogId, workTypeCode, worker?.partnerId, ccKey, content, photoKey, includedKey]);
  const sentRecipientNames = useMemo(
    () =>
      (result?.sendResults ?? [])
        .map((r) => allRecipients.find((p) => p.partnerId === r.partnerId)?.partnerName)
        .filter((n): n is string => !!n)
        .join(', '),
    [result, allRecipients],
  );
  // 현장 선택 기본정보(날씨+스케줄) — 선택 현장이 바뀔 때마다 site-info 조회(§2.12)
  const [siteInfo, setSiteInfo] = useState<WorkOrderSiteInfo | null>(null);
  // 현장 선택 후 site-info(날씨·스케줄 반장) 조회 중 여부 — 수신자 영역 잠금 + 조회중 토스트
  const [loadingSite, setLoadingSite] = useState(false);

  /**
   * 스케줄 공정 → BasicInfoPartner 변환(작업자 자동배정 case A 등에 재사용).
   * SIF-1 '자재파트너가 시공' 공종이면 시공반장 대신 그 공정의 자재파트너(발주 거래처)를 작업자로 쓴다 — 발주가 없으면 null.
   */
  const toPartner = (wt: WorkOrderSiteWorkType): BasicInfoPartner | null => {
    if (wt.constructByVendor === true) {
      return wt.materialPartnerId == null
        ? null
        : { partnerId: wt.materialPartnerId, partnerName: wt.materialPartnerName ?? `#${wt.materialPartnerId}`, partnerPhone: wt.materialPartnerContact ?? null, email: null, workTypeCodes: null };
    }
    return wt.partnerId == null
      ? null
      : { partnerId: wt.partnerId, partnerName: wt.partnerName ?? `#${wt.partnerId}`, partnerPhone: wt.partnerContact ?? null, email: null, workTypeCodes: null };
  };

  // 사진 미리보기 objectURL 정리용 최신값 ref — 아래 초기화 effect·언마운트 정리 둘 다에서 재사용.
  const photosRef = useRef(photos);
  photosRef.current = photos;

  /** 선택 공정의 작업 요청 메모(R-19) — 공정이 없거나 메모가 없으면 null */
  const taskMemo = taskId != null ? (siteInfo?.schedule?.workTypes?.find((wt) => wt.taskId === taskId)?.scheduleMemo ?? null) : null;
  // 공종이 바뀔 때(선택·자동배정·현장 변경) — 요청 체크를 처음 상태(전부 켜짐)로 되돌리고, 작업 요청내용은 규칙(S16-6)대로 메모를 채운다.
  useEffect(() => {
    setExcludedReqs(new Set());
    const r = applyMemoOnTaskChange(contentRef.current, loadedMemoRef.current, taskMemo);
    loadedMemoRef.current = r.loadedMemo;
    if (r.replaced) setContent(r.content);
  }, [taskId, taskMemo]);
  const reloadMemo = () => {
    const memo = taskMemo ?? '';
    loadedMemoRef.current = memo;
    setContent(memo);
  };

  // 현장·요청일 변경 시 전체 초기화(공종·작업자·참조인·사진·내용, §5.4.1) — 조회 동안은 '조회 중'으로 잠근다.
  useEffect(() => {
    setWorkTypeCode('');
    setTaskId(null);
    setWorker(null);
    setCcList([]);
    setContent('');
    photosRef.current.forEach(revokePreview);
    setPhotos([]);
    if (!selected) {
      setSiteInfo(null);
      setLoadingSite(false);
      return;
    }
    const controller = new AbortController();
    setSiteInfo(null);
    setLoadingSite(true);
    // 주소로 지정해 연 공정 — 그 현장의 첫 조회에서만. 서버는 오늘 이후 목록에 없는 공정(이미 끝난 공정)도 이 공정만큼은 목록에 넣어 준다.
    const wantTaskId = !linkTaskApplied.current && linkSiteId === selected.siteId ? linkTaskId : null;
    worklogApi
      .getWorkOrderSiteInfo(
        {
          siteId: selected.siteId,
          siteAddress: selected.siteAddress,
          siteLat: selected.siteLat, // 현장 GPS 위도(날씨 격자변환 기준) — worklogStore basic-info에서 취득
          siteLng: selected.siteLng,
          workDate: today.ymd, // 오늘 이후 공정 전부 받기(종료일 ≥ 오늘 — 진행 중 + 착공 전, §6.6) — 케이스 A·B·C 판정 기준
          taskId: wantTaskId ?? undefined,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        const info = res.data.data;
        setSiteInfo(info);
        const tasks = info.schedule?.workTypes ?? [];
        // 주소로 지정한 공정이 목록에 있으면 그 공종 + 담당 시공반장을 채운다(REQ-11-03). 적용했으면 다시 쓰지 않는다 —
        // 조회가 취소돼 여기까지 못 온 경우(StrictMode 재실행 · 현장 재선택)에는 표시를 남기지 않아 다음 조회가 이어받는다.
        const linked = wantTaskId != null ? tasks.find((wt) => wt.taskId === wantTaskId) : undefined;
        if (wantTaskId != null) linkTaskApplied.current = true;
        if (linked) {
          setWorkTypeCode(linked.workTypeCode);
          setTaskId(linked.taskId);
          setWorker(toPartner(linked));
        } else if (tasks.length === 1) {
          // 공종·작업자 자동배정 케이스 (a) — 스케줄 공정이 정확히 1개면 그 공종+작업자를 자동 채운다.
          const wt = tasks[0];
          setWorkTypeCode(wt.workTypeCode);
          setTaskId(wt.taskId);
          setWorker(toPartner(wt));
        }
        setLoadingSite(false);
      })
      .catch((err) => {
        // 날씨/스케줄은 부가정보 — 실패해도 작성 흐름은 계속. 취소(현장 재변경)면 무시.
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setLoadingSite(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  /** 케이스 판정 — 스케줄 공정 있으면 그 목록만(case1), 없으면 공종마스터 전체(case2). */
  const scheduleWorkTypes = siteInfo?.schedule?.workTypes ?? [];
  const hasSchedule = scheduleWorkTypes.length > 0;
  const codes = useAppStore((s) => s.codes);
  const masterWorkTypes = useMemo(
    () => codes.filter((c) => c.codeGroupCode === 'ZEU_WORK_TYPE').sort((a, b) => a.codeOrder - b.codeOrder),
    [codes],
  );
  const workTypeOptions = useMemo<WorkTypeSelectOption[]>(() => {
    if (!hasSchedule) return masterWorkTypes.map((c) => ({ value: c.codeValue, label: c.codeName }));
    const options = scheduleWorkTypes.map((wt) => {
      const status = partnerStatusView(wt);
      return {
        // 공정ID — 같은 공종이 두 번 나오는 스케줄(오늘 공정 + 주소로 지정해 연 다른 날 공정 등)도 구분되도록 코드가 아니라 공정으로 가른다
        value: String(wt.taskId),
        // SIF-1 '자재파트너가 시공' 공종은 '공종명 · 자재거래처명'(자재거래처가 없으면 공종명만)
        label: wt.constructByVendor === true && wt.materialPartnerName ? `${wt.workTypeName} · ${wt.materialPartnerName}` : wt.workTypeName,
        // 담당 시공반장 — 매칭 안 된 공정은 null('미배정' 배지만)
        partnerName: wt.partnerId != null ? (wt.partnerName ?? null) : null,
        status,
      };
    });
    // 반장 일정이 확정된 공종을 위로, 그 밖(회신 대기 · 미확정 · 미배정)은 아래로 — 같은 묶음 안에서는 스케줄 순서 그대로.
    // 상태 값이 없는 공정(status=null)은 확정과 같은 묶음에 둬서 예전 순서를 바꾸지 않는다.
    return options.sort((a, b) => Number(a.status != null && !a.status.confirmed) - Number(b.status != null && !b.status.confirmed));
  }, [hasSchedule, scheduleWorkTypes, masterWorkTypes]);
  /** 목록 상단 안내문구 — 스케줄 공정이면 그 안내, 아니면 마스터 안내(스케줄은 있으나 오늘 이후 공정이 없는 경우는 문구를 따로 둔다) */
  const workTypeHeader = hasSchedule
    ? WORK_TYPE_HEADER_SCHEDULE
    : siteInfo?.schedule?.scheduleId != null
      ? WORK_TYPE_HEADER_NO_TODAY
      : WORK_TYPE_HEADER_NO_SCHEDULE;

  /**
   * 선택한 공정의 반장 일정 상태 — 확정이 아니면 작업공종 줄 아래에 경고를 보인다. 미확정 공종도 고르고 보낼 수 있다(막지 않음).
   * 공종마스터에서 고른 경우(공정 없음)와 상태 값이 없는 경우는 null.
   */
  const selectedTask = taskId != null ? scheduleWorkTypes.find((wt) => wt.taskId === taskId) : undefined;
  const selectedPartnerStatus = selectedTask ? partnerStatusView(selectedTask) : null;
  /** 선택 공종이 '자재파트너가 시공'(SIF-1)인지 — sv-planner 플래그를 BE가 전파한 constructByVendor 단일 기준(partnerRole 아님). */
  const taskConstructByVendor = selectedTask?.constructByVendor === true;
  /** 자재시공 공종의 자재파트너(작업자 후보 1명) — 자재시공이 아니거나 발주가 없으면 null. */
  const materialWorker = taskConstructByVendor && selectedTask ? toPartner(selectedTask) : null;
  /** 작업자가 그 공정의 자재파트너인지 — '자재파트너' 배지 표시 조건. */
  const workerIsMaterialPartner = !!worker && !!materialWorker && materialWorker.partnerId === worker.partnerId;
  /** [반장님 확정하기 ›] — 작성 중인 내용이 사라지지 않도록 새 탭에서 인디플래너 시공파트너배정 팝업을 연다. */
  const openPartnerConfirm = () => {
    if (!selected || !selectedTask) return;
    window.open(ganttPartnerAssignPath(selected.siteId, selectedTask.taskId), '_blank', 'noopener,noreferrer');
  };

  /** 셀렉박스 값 — 스케줄 공정 목록이면 공정ID, 공종마스터 목록이면 공종코드(workTypeOptions의 value와 같은 기준) */
  const workTypeSelectValue = hasSchedule && taskId != null ? String(taskId) : workTypeCode;

  /**
   * 공종 선택(케이스 1·2 공통 트리거) — case1(공정 목록)이면 value=공정ID라 그 공정과 담당 시공반장으로 작업자 자동배정(case A),
   * case2(공종마스터)면 value=공종코드이고 작업자는 비운다(case B/C).
   */
  const pickWorkType = (value: string) => {
    const task = hasSchedule ? scheduleWorkTypes.find((wt) => String(wt.taskId) === value) : undefined;
    setWorkTypeCode(task ? task.workTypeCode : value);
    setTaskId(task?.taskId ?? null);
    setWorker(task ? toPartner(task) : null);
  };

  /**
   * 작업자 후보 — 참조인으로 이미 고른 사람은 제외(중복 배정 방지) +
   * 선택된 공종을 전문분야로 등록한 파트너만(전문분야 미등록 파트너는 그대로 노출).
   * Planner(SigongPopup)의 반장배정 필터와 동일 기준 — 전문분야 불일치 배정을 원천에서 막아
   * "배정은 됐는데 Planner 화면엔 안 뜸" 류의 불일치를 예방한다. 참조인은 이 제약을 두지 않는다.
   * SIF-1 '자재파트너가 시공' 공종이면 그 공정의 자재파트너 1명만(시공반장 목록 노출 안 함, 발주 없으면 빈 목록).
   */
  const workerOptions = useMemo<WorkerSelectOption[]>(() => {
    // 둘째 줄 — 시공파트너: 전문분야 · 연락처 / 자재파트너: '자재파트너' · 공종 · 연락처 (빈 값은 생략)
    const line = (parts: (string | null | undefined)[]) => parts.filter((s) => !!s && s.trim()).join(' · ');
    if (taskConstructByVendor) {
      return materialWorker
        ? [{ partner: materialWorker, material: true, detail: line(['자재파트너', selectedTask?.workTypeName, materialWorker.partnerPhone]) }]
        : [];
    }
    const toOption = (p: BasicInfoPartner): WorkerSelectOption => ({
      partner: p,
      material: false,
      detail: line([
        p.workTypeCodes ? p.workTypeCodes.split(',').map((code) => workTypeNameOf.get(code) ?? code).join(', ') : null,
        p.partnerPhone,
      ]),
    });
    const filtered = partners
      .filter((p) => !ccList.some((c) => c.partnerId === p.partnerId))
      .filter((p) => !workTypeCode || !p.workTypeCodes || p.workTypeCodes.split(',').includes(workTypeCode))
      .map(toOption);
    // 지금 선택된 작업자(공정 담당 반장 자동 선택 포함)는 전문분야 필터와 상관없이 목록에 항상 둔다 — 닫힌 칸과 목록이 어긋나지 않게
    if (worker && !filtered.some((o) => o.partner.partnerId === worker.partnerId)) {
      const full = partners.find((p) => p.partnerId === worker.partnerId) ?? worker;
      return [toOption(full), ...filtered];
    }
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskConstructByVendor, materialWorker?.partnerId, materialWorker?.partnerName, materialWorker?.partnerPhone, selectedTask?.workTypeName, partners, ccList, workTypeCode, workTypeNameOf, worker]);
  // 공종을 골랐는데 작업자가 비어 있고 후보가 딱 1명이면 그 사람을 기본 선택한다.
  useEffect(() => {
    if (workTypeCode && !worker && workerOptions.length === 1) setWorker(workerOptions[0].partner);
  }, [workTypeCode, worker, workerOptions]);
  /**
   * 시공반장 교체 경고 — 선택한 공종(스케줄 공정)에 이미 배정된 반장이 있고 작업자를 다른 사람으로 고르면 안내한다.
   * 현재 반장 출처 = site-info 공정(wt.partnerId/partnerName). 공종 미선택·공종마스터 선택(공정 없음)·기존 반장 없음·같은 반장이면 null.
   * 발송은 막지 않는다(알림 용도).
   */
  const foremanChangeWarning = useMemo(() => {
    if (!worker || !workTypeCode) return null;
    const task =
      (taskId != null ? scheduleWorkTypes.find((wt) => wt.taskId === taskId) : undefined) ??
      scheduleWorkTypes.find((wt) => wt.workTypeCode === workTypeCode);
    if (!task || task.partnerId == null || task.partnerId === worker.partnerId) return null;
    return `작업자가 ${task.partnerName?.trim() || '기존 작업자'}에서 ${worker.partnerName}로 변경됩니다.`;
  }, [worker, workTypeCode, taskId, scheduleWorkTypes]);
  const ccOptions = useMemo(
    () => partners.filter((p) => p.partnerId !== worker?.partnerId).map((p) => ({ value: String(p.partnerId), label: p.partnerName })),
    [partners, worker],
  );

  /** "새 시공반장 등록"(공용 PartnerFormPopup) 저장 완료 — 목록 갱신 후 연 쪽(작업자/참조인)에 바로 배정한다. */
  const handlePartnerCreated = async (partnerId: number) => {
    setAddPartnerFor(null);
    await useWorklogStore.getState().refresh();
    const created = useWorklogStore.getState().partners.find((p) => p.partnerId === partnerId);
    if (!created) return;
    if (addPartnerFor === 'worker') setWorker(created);
    else if (addPartnerFor === 'cc') setCcList((prev) => [...prev, created]);
  };

  // object URL 정리(언마운트 시) — photosRef는 위 초기화 effect와 공유.
  useEffect(() => {
    return () => {
      photosRef.current.forEach(revokePreview);
    };
  }, []);

  const dirty = content.trim().length > 0 || photos.length > 0 || !!worker || ccList.length > 0;
  // 발송 조건(§5.4.1 룰) — ① 현장 선택 ② 지시 내용 입력 ③ 작업자 1명. 참조인은 무관.
  // 작업 요청내용 · 고객 요청 중 하나라도 있으면 보낼 수 있다(941 S16-6)
  const canSend = canSendSingle({ hasSite: !!selected, content, includedCount: includedReqs.length, hasWorker: !!worker, sending, reqBlocked });

  const chooseSite = (w: BasicInfoWorklog) => {
    if (selected && selected.worklogId !== w.worklogId && dirty) {
      setPendingSite(w);
      return;
    }
    setSelected(w);
  };

  // 현장 변경 확정 — 폼 전체 초기화는 selected 변경 effect가 담당(§5.4.1).
  const confirmChangeSite = () => {
    if (pendingSite) {
      setSelected(pendingSite);
      setPendingSite(null);
    }
  };

  // basic-info 재조회 후 새로 생성된 worklog를 찾아 선택
  const selectCreatedWorklog = async (siteId: number) => {
    await useWorklogStore.getState().refresh();
    const w = useWorklogStore.getState().worklogs.find((x) => x.siteId === siteId);
    if (w) chooseSite(w);
  };

  /**
   * site만 있는 현장(worklogId=null) 선택 — schedule-lookup 조회 후 분기.
   * 공사기간(시작·종료일)이 둘 다 있으면 팝업 없이 바로 공사현황을 생성하고,
   * 하나라도 없으면 공사기간 입력 팝업(Step D)을 띄운다.
   */
  const handleSelectSiteOnly = async (opt: SiteOption) => {
    if (memberId == null) {
      alert('로그인 정보를 확인할 수 없습니다. 다시 로그인해주세요.');
      return;
    }
    setScheduleLookupLoading(true);
    try {
      const res = await worklogApi.getScheduleLookup(opt.siteId);
      const period = res.data.data;
      if (period.constStartDate && period.constEndDate) {
        await worklogApi.createWorklog({
          siteId: opt.siteId,
          memberId,
          scheduleId: period.scheduleId ?? null,
          constStartDate: period.constStartDate,
          constEndDate: period.constEndDate,
        });
        await selectCreatedWorklog(opt.siteId);
      } else {
        setPeriodPopupSite(opt);
      }
    } catch {
      alert('공사기간 조회에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setScheduleLookupLoading(false);
    }
  };

  // 주소로 지정한 현장에 공사현황이 아직 없으면(현장만 있음, worklogId=null) 목록에서 고른 것과 같이
  // 공사현황을 만든 뒤 연다 — 만든 공사현황이 선택되면 위 현장 조회가 주소의 공정(taskId)까지 채운다(한 번만).
  const linkSiteOnlyApplied = useRef(false);
  useEffect(() => {
    if (linkSiteOnlyApplied.current || linkSiteApplied.current || linkSiteId == null || selected != null) return;
    if (worklogs.some((x) => x.siteId === linkSiteId)) return; // 공사현황이 있으면 위 effect 가 처리
    const opt = siteOptions.find((o) => o.siteId === linkSiteId && o.worklogId == null);
    if (!opt) return; // 기준정보가 아직 안 왔거나 열 수 없는 현장 — 평소처럼 연다
    linkSiteOnlyApplied.current = true;
    void handleSelectSiteOnly(opt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkSiteId, worklogs, siteOptions, selected]);

  // 공사기간 입력 팝업(Step D) 완료 → 새로 생성된 worklog 선택
  const handlePeriodPopupComplete = async (siteId: number) => {
    setPeriodPopupSite(null);
    await selectCreatedWorklog(siteId);
  };

  // 키워드는 한 줄에 하나씩 쓰는 입력 패턴이라, 삽입 후 커서를 다음 줄로 내린다.
  // 삽입 지점 뒤가 이미 줄바꿈이면 새로 넣지 않고 그 줄바꿈을 건너뛰기만 한다(빈 줄 방지).
  const insertKeyword = (text: string) => {
    const ta = textRef.current;
    if (!ta) {
      setContent((c) => (c.endsWith('\n') || !c ? c + text + '\n' : `${c}\n${text}\n`));
      return;
    }
    const start = ta.selectionStart ?? content.length;
    const end = ta.selectionEnd ?? content.length;
    const after = content.slice(end);
    const needsBreak = !after.startsWith('\n');
    const inserted = needsBreak ? `${text}\n` : text;
    setContent(content.slice(0, start) + inserted + after);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + inserted.length + (needsBreak ? 0 : 1);
      ta.setSelectionRange(pos, pos);
    });
  };

  const addPhotos = (files: File[]) => {
    // 공통 검증(개수·용량) — uploadPolicy 한곳에서 처리. 초과분 제외 후 토스트 안내.
    const result = pickValidPhotos(files, photos.length);
    if (result.accepted.length) {
      setPhotos((p) => [
        ...p,
        ...result.accepted.map((f): LocalPhoto => {
          const url = URL.createObjectURL(f);
          return { id: url, url, name: f.name, source: 'FILE', file: f };
        }),
      ]);
    }
    const notice = photoDropNotice(result);
    if (notice) setToastMsg(notice);
  };

  /** 인디포토 사진첩에서 고른 사진 첨부 — 파일 업로드 없이 photoId 로 등록된다. */
  const addIndiPhotos = (picked: PickedIndiPhoto[]) => {
    setIndiPhotoOpen(false);
    if (!picked.length) return;
    const room = Math.max(0, PHOTO_POLICY.maxCount - photos.length);
    const within = picked.filter((p) => !photos.some((x) => x.photoId === p.photoId)).slice(0, room);
    if (!within.length) return;
    setPhotos((prev) => [
      ...prev,
      ...within.map((p): LocalPhoto => ({
        id: `indi-${p.photoId}`,
        url: p.thumbUrl ?? '',
        name: `인디포토 ${p.photoId}`,
        source: 'INDI_PHOTO',
        photoId: p.photoId,
        fileId: p.fileId ?? null,
      })),
    ]);
    setToastMsg(`인디포토 사진 ${within.length}장을 첨부했습니다.`);
  };

  const removePhoto = (id: string) => {
    setPhotos((p) => {
      const t = p.find((x) => x.id === id);
      if (t) revokePreview(t);
      return p.filter((x) => x.id !== id);
    });
  };

  // 현장 해제 — 폼 전체 초기화는 selected 변경 effect가 담당(§5.4.1).
  const clearAll = () => {
    if (taskId != null) {
      // 공정을 고른 상태 — 입력란은 작업 요청 메모로 다시 채우고 요청 체크·사진은 처음 상태로 되돌린다(941 S16-6)
      reloadMemo();
      setExcludedReqs(new Set());
      photosRef.current.forEach(revokePreview);
      setPhotos([]);
      return;
    }
    setSelected(null);
  };

  const send = async () => {
    if (!selected || !canSend) return;
    setSending(true);
    try {
      // 사진 메타(photoSeq 순) + 실제 파일(photoFiles[]) — 순서 대응(BE §2.3).
      // 인디포토 선택분은 파일 없이 photoId 만 보낸다(fileId xor photoId) → photoFiles 에서도 제외.
      const photoMeta = photos.map((p, i): WorkOrderPhotoMeta => {
        if (p.source === 'INDI_PHOTO') {
          // fileId 는 인디포토 목록이 내려줄 때만 담긴다 — 있으면 photoId 와 함께 저장(FILE_ID·PHOTO_ID 동시 등록).
          return {
            photoSeq: i,
            source: 'INDI_PHOTO',
            photoId: p.photoId!,
            ...(p.fileId != null ? { fileId: p.fileId } : {}),
          };
        }
        const dot = p.name.lastIndexOf('.');
        return {
          photoSeq: i,
          source: 'FILE',
          fileName: p.name,
          fileExt: dot >= 0 ? p.name.slice(dot + 1).toLowerCase() : '',
          fileSize: p.file!.size,
        };
      });
      const photoFiles = photos
        .filter((p) => p.source === 'FILE' && p.file)
        .map((p) => p.file as File);
      if (!worker) return; // canSend가 이미 보장하지만 타입 좁히기용
      const res = await worklogApi.createWorkOrder(selected.worklogId, {
        requestKey,
        workTypeCode,
        taskId: taskId ?? undefined,
        orderContent: content.trim(),
        customerRequests: taskId != null && includedReqs.length ? toRefs(includedReqs) : undefined,
        recipients: [
          {
            partnerId: worker.partnerId,
            role: 'WORKER' as const,
            // 자재파트너면 수신자 이름은 담당자명 우선(일괄 작업요청 BulkWorkOrderService와 같은 기준), 없으면 자재파트너명.
            partnerName: (workerIsMaterialPartner && selectedTask?.materialPartnerContactName?.trim()) || worker.partnerName,
            mobile: worker.partnerPhone ?? undefined,
          },
          ...ccList.map((r) => ({
            partnerId: r.partnerId,
            role: 'CC' as const,
            partnerName: r.partnerName,
            mobile: r.partnerPhone ?? undefined,
          })),
        ],
        compName: useAppStore.getState().session?.compName ?? undefined,
        photos: photoMeta.length ? photoMeta : undefined,
        photoFiles: photoFiles.length ? photoFiles : undefined,
        // SIF-1 — 선택 공종이 '자재파트너가 시공'이면 true 전달 → BE가 시공반장 확정 write-back 을 건너뛴다.
        materialConstruction: taskConstructByVendor ? true : undefined,
      });
      setResult(res.data.data);
      void qc.invalidateQueries({ queryKey: [CUST_REQ_KEY] });
    } catch (err) {
      if (isCustReqConflict(err)) {
        // 선택한 요청이 그 사이 바뀌었거나 삭제됨(전체 거부) — 목록을 다시 불러와 보이게 한다
        toast.error(custReqErrorMessage(err, CUST_REQ_ORDER_MESSAGE.conflictFallback));
        void qc.invalidateQueries({ queryKey: [CUST_REQ_KEY] });
      } else {
        alert('작업요청 작성에 실패했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setSending(false);
    }
  };

  const hint = !selected
    ? '현장을 선택해주세요'
    : content.trim().length === 0 && includedReqs.length === 0
      ? CUST_REQ_ORDER_MESSAGE.noContent
      : reqBlocked
        ? custReqQuery.isError
          ? CUST_REQ_ORDER_MESSAGE.reqLoadFailed
          : CUST_REQ_ORDER_MESSAGE.reqChecking
        : !worker
        ? '작업자를 1명 선택해주세요'
        : '작성 완료 — 카카오톡으로 발송할 수 있어요';

  // 스케줄 연동 현장을 조회 중 — 조회중 안내(지속 info 토스트)로 대기 유도
  const scheduleLoading = loadingSite && selected?.scheduleId != null;

  return (
    <div className="flex h-full flex-col bg-surface">
      <Breadcrumb items={[{ label: '작업요청' }, { label: '작성' }]} className="px-06 pt-04" />
      <WorkOrderTabs />
      {scheduleLookupLoading ? (
        <Toast
          message="공사기간을 조회 중입니다. 잠시만 기다려 주세요."
          variant="info"
          duration={null}
          onClose={() => {}}
        />
      ) : scheduleLoading ? (
        <Toast
          message="스케줄 정보를 조회 중입니다. 잠시만 기다려 주세요."
          variant="info"
          duration={null}
          onClose={() => {}}
        />
      ) : (
        <Toast message={toastMsg} onClose={() => setToastMsg(null)} />
      )}
      {/* 상단 메타(날짜 + 오늘 날씨) */}
      <div className="flex shrink-0 items-center gap-3 px-06 pt-05 text-body-md">
        <span className="font-medium">
          <span className="text-text">{today.date} </span>
          <span style={{ color: weekdayColor }}>({today.weekday})</span>
          {todayIsHoliday && holidayName && (
            <span className="ml-1.5" style={{ color: 'var(--color-error)' }}>
              {holidayName}
            </span>
          )}
        </span>
        {siteInfo?.weatherNow && siteInfo.weatherNow.tempNow != null && (
          <span className="flex items-center gap-1.5 text-text-secondary">
            {siteInfo.weatherNow.weatherIcon && (
              <span className="text-base leading-none">{siteInfo.weatherNow.weatherIcon}</span>
            )}
            {siteInfo.weatherNow.weatherName && <span>{siteInfo.weatherNow.weatherName}</span>}
            <span className="font-semibold text-text">{siteInfo.weatherNow.tempNow}°</span>
            {siteInfo.weatherNow.tempLow != null && (
              <span className="text-[color:var(--color-infoStrong)]">↓ {siteInfo.weatherNow.tempLow}°</span>
            )}
            {siteInfo.weatherNow.tempHigh != null && (
              <span className="text-error">↑ {siteInfo.weatherNow.tempHigh}°</span>
            )}
          </span>
        )}
      </div>

      {/* 현장 선택 + 액션 (풀폭 한 줄) */}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 px-6 pb-3 pt-2.5">
        <SiteSelect
          siteOptions={siteOptions}
          status={storeStatus}
          selected={selected}
          onSelectWorklog={chooseSite}
          onSelectSiteOnly={handleSelectSiteOnly}
        />
        {selected ? (
          /*
            선택 현장의 고객명·주소 — basic-info(selected)에 이미 있는 값이라
            siteInfo(§2.12) 응답을 기다리지 않고 즉시 표시한다.
          */
          <span className="flex min-w-0 items-center gap-5 text-body-sm text-text-secondary">
            <span className="flex shrink-0 items-center gap-1.5">
              {/* D-07 §20.1 icon-sm(16) · §20.2 stroke 2 */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="text-text-muted">
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="font-medium text-text">{selected.customerName ?? '-'}</span>
            </span>
            <span className="flex min-w-0 items-center gap-1.5">
              {/*
                접힌 종이지도 + 위치핀 — 지도만 두면 접힌 종이/아코디언으로 오인돼서
                핀을 얹어 '주소'로 바로 읽히게 한다. 핀은 면(fill)으로 채워 라인 지도 위에서 도드라진다.
              */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-muted">
                {/* 접힌 종이지도(3면 닫힌 형태) — 핀 자리를 벌어주려 살짝 왼쪽·아래로 */}
                <path d="M7 9.2L1.5 11.4V22L7 19.8l6 2.2 5-2.2V9.2L13 11.4 7 9.2z" />
                <path d="M7 9.2v10.6" />
                <path d="M13 11.4V22" />
                {/* 핀 자리 비우기 — 배경색으로 지도 선을 도려내야 핀이 겹쳐 뭉개지지 않는다 */}
                <circle cx="18.4" cy="4.6" r="4.4" fill="var(--shell-bg)" stroke="none" />
                <path d="M18.4 4.6V14.5" stroke="var(--shell-bg)" strokeWidth={4.5} />
                {/* 위치핀 — 원형 머리 + 곧은 침(첨부 이미지 형태) */}
                <circle cx="18.4" cy="4.6" r="2.4" />
                <path d="M18.4 7V14" />
              </svg>
              <span className="truncate" title={selected.siteAddress ?? undefined}>
                {selected.siteAddress ?? '주소 미등록'}
              </span>
            </span>
          </span>
        ) : null}
      </div>

      {/* 작성 폼 — 남은 높이 채움(풀폭) */}
      <div className="flex min-h-0 flex-1 flex-col px-6 pb-5">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg border border-border bg-surface-raised">
          {/* 현장 조회 중엔 공종·작업자·참조인 전부 잠금(자동배정 전에 미리 골라 재설정되는 혼란 방지) */}
          {selected && loadingSite ? (
            <Row label="작업공종">
              <span className="mt-0.5 flex items-center gap-2 text-body-md text-text-secondary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="animate-spin" aria-hidden>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                {selected.scheduleId != null ? '스케줄 정보를 조회 중입니다…' : '현장 정보를 조회 중입니다…'}
              </span>
            </Row>
          ) : (
            <>
              {/* 작업공종 — 1개만 선택(REQ-1-02). case1(스케줄 있음)은 그 공정만, case2(없음)는 공종마스터 전체 */}
              <Row label="작업공종">
                <div className="flex min-w-0 flex-1 flex-col gap-sm">
                  <div className="flex flex-wrap items-center gap-sm">
                    <WorkTypeSelect
                      value={workTypeSelectValue}
                      onChange={pickWorkType}
                      options={workTypeOptions}
                      header={workTypeHeader}
                      disabled={!selected}
                      placeholder={selected ? '작업공종 선택' : '현장을 먼저 선택하세요'}
                      emptyText="선택 가능한 공종이 없습니다"
                    />
                    {/*
                      안내 문구 — 공종 셀렉박스 같은 줄 오른쪽. 공종이 정해지기 전까지 항상 노출(기획 html: !workTypeCode 기준),
                      배경·테두리 없는 아이콘 + 글자(D-09 §15.34 StatusHint).
                    */}
                    {!workTypeCode && (
                      <StatusHint
                        tone="info"
                        className="shrink-0"
                        icon={
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="13" />
                            <circle cx="12" cy="17" r="1" fill="currentColor" stroke="none" />
                          </svg>
                        }
                      >
                        작업공종을 선택하면 담당 작업자(시공파트너·자재파트너)가 자동 추가됩니다.
                      </StatusHint>
                    )}
                  </div>
                  {/* 반장 일정이 확정되지 않은 공종 — 막지 않고 알린다(REQ-11-01). 표준 Alert(D-09 §15.10) + 우측 액션 슬롯 */}
                  {selectedTask && selectedPartnerStatus && !selectedPartnerStatus.confirmed && (
                    <Alert
                      kind="warning"
                      role="status"
                      action={
                        <Button type="button" variant="etc" size="sm" onClick={openPartnerConfirm}>
                          반장님 확정하기 ›
                        </Button>
                      }
                    >
                      {/* .alert-body 는 자식마다 한 줄씩 쌓는 구조 — 문장이 이어지도록 한 덩어리(span)로 감싼다 */}
                      <span>
                        <strong>{selectedTask.workTypeName}</strong> — 반장님 일정이 아직 확정되지 않았어요 (
                        {[selectedTask.partnerId != null ? selectedTask.partnerName?.trim() : null, selectedPartnerStatus.label]
                          .filter(Boolean)
                          .join(' · ')}
                        ). 그래도 보낼 수 있어요.
                      </span>
                    </Alert>
                  )}
                </div>
              </Row>
              {/* 공종 전달 카드 — 선택 공정의 고객 요청 체크 목록(S16-6). 공정이 없거나 요청이 없으면 영역 자체를 숨긴다(안내 없음) */}
              {taskId != null && (reqStatus !== 'ready' || receivedReqs.length > 0) && (
                <Row label="고객 요청">
                  <CustReqDeliveryCard
                    className="flex-1"
                    items={receivedReqs}
                    excluded={excludedReqs}
                    onToggle={(id) => setExcludedReqs((prev) => toggleExcluded(prev, id))}
                    status={reqStatus}
                    onRetry={() => void custReqQuery.refetch()}
                  />
                </Row>
              )}
              {/* 작업자 — 필수 1명(REQ-1-04). 공종 매칭 시 자동배정(case A: 시공파트너 또는 자재시공 공종의 자재파트너), 없으면 직접 지정(case B) */}
              <Row label="작업자">
                <div className="flex flex-1 flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  {/* 작업공종 선택칸과 같은 셀렉박스 — 선택칸: 파트너명(+자재파트너 배지) / 목록: 이름(+배지) · 전문분야 · 연락처 */}
                  <Temp_WorkerSelect
                    value={worker}
                    valueIsMaterial={workerIsMaterialPartner}
                    options={workerOptions}
                    // 작업공종을 먼저 골라야 작업자를 고를 수 있다(공종에 따라 후보가 달라짐)
                    disabled={!selected || !workTypeCode}
                    placeholder={workTypeCode ? '작업자를 선택해주세요 (시공파트너 · 자재파트너)' : '작업공종을 먼저 선택해주세요'}
                    emptyText={taskConstructByVendor ? '지정된 자재파트너가 없습니다' : '등록된 작업자가 없습니다'}
                    onChange={setWorker}
                  />
                  {/* 자재시공 공종은 작업자가 자재파트너로 정해져 시공파트너 새 등록을 두지 않는다 */}
                  {!taskConstructByVendor && (
                    <Button type="button" variant="secondary" size="sm" onClick={() => setAddPartnerFor('worker')}>
                      + 새 시공파트너 등록
                    </Button>
                  )}
                </div>
                {/* 시공반장 교체 경고 — 같은 화면의 안내(StatusHint §15.34)와 같은 컴포넌트, tone=warning(아이콘만 status.warning) */}
                {foremanChangeWarning && (
                  <StatusHint
                    tone="warning"
                    role="status"
                    icon={
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    }
                  >
                    {foremanChangeWarning}
                  </StatusHint>
                )}
                </div>
              </Row>
              {/* 참조인 — 여러 명, 선택(REQ-1-06) */}
              <Row label="참조인">
                <div className="flex flex-1 items-center gap-2">
                  <MultiSelect
                    className="!max-w-none flex-1"
                    searchable
                    values={ccList.map((p) => String(p.partnerId))}
                    onChange={(vals) => setCcList(vals.map((v) => partners.find((p) => String(p.partnerId) === v)).filter((p): p is BasicInfoPartner => !!p))}
                    options={ccOptions}
                    disabled={!selected}
                    placeholder="참조인을 선택해주세요"
                    emptyText="등록된 작업자가 없습니다"
                  />
                  <Button type="button" variant="secondary" size="sm" onClick={() => setAddPartnerFor('cc')}>
                    + 새 시공파트너 등록
                  </Button>
                </div>
              </Row>
            </>
          )}
          {/* 사진첨부 */}
          <Row label="사진첨부">
            <PhotoAttach
              photos={photos}
              onAdd={addPhotos}
              onRemove={removePhoto}
              onOpen={setLightbox}
              onOpenIndiPhoto={() => setIndiPhotoOpen(true)}
            />
          </Row>
          {/* 내 키워드 */}
          <Row label="내 키워드">
            <KeywordChips onInsert={insertKeyword} />
          </Row>

          {/* 작업 요청내용 머리말 — 공종 메모 불러옴/직접 고침 안내 + 다시 불러오기(S16-6) */}
          <div className="flex shrink-0 flex-wrap items-center gap-sm px-4 pt-3">
            <span className="text-body-sm font-medium text-text-secondary">{CUST_REQ_ORDER_MESSAGE.contentLabel}</span>
            <StatusHint tone="muted">{memoHintText(memoHintKind(taskId != null, taskMemo, content))}</StatusHint>
            {canReloadMemo(taskMemo, content) && (
              <TextLink onClick={reloadMemo}>{CUST_REQ_ORDER_MESSAGE.memoReload}</TextLink>
            )}
          </div>

          {/* 요청 내용 — 남은 높이 채움(최소 높이 확보, 부족하면 카드 자체가 스크롤). Textarea(forwardRef)로 커서 위치 삽입 로직(textRef) 유지 */}
          <div className="flex min-h-13 flex-1 flex-col p-4">
            <Textarea
              ref={textRef}
              fill
              textareaClassName="resize-none"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="✏️ 작업자에게 전달할 작업 내용을 입력하세요..."
            />
          </div>

          {/* 힌트 — 카드가 스크롤돼도 항상 보이게 하단 고정 */}
          <div className="sticky bottom-0 flex shrink-0 items-center gap-sm border-t border-border bg-surface-raised px-05 py-2.5 text-body-sm text-text-secondary">
            <span>💬</span>
            <span>{hint}</span>
          </div>
        </div>
      </div>

      {/* 발송 액션 — 폼 하단 고정, 버튼 스타일·아이콘 화면기획서 실측(작업요청_등록_현장선택.html) 그대로 */}
      <div className="flex shrink-0 items-center justify-end gap-sm border-t border-border px-06 py-04">
        <Button variant="etc" size="sm" onClick={clearAll}>
          ↩ 초기화
        </Button>
        <Button variant="etc" size="sm" disabled={!canSend} onClick={() => setPreviewOpen(true)}>
          미리보기
        </Button>
        <Button variant="success" size="sm" disabled={!canSend} onClick={send}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          {sending ? '발송 중…' : '카카오톡 발송'}
        </Button>
      </div>

      {/*
        크게보기 — 공통 모듈(줌·1:1·드래그 이동 포함).
        파일 첨부분은 objectURL이라 기본 <img>, 인디포토분은 인증이 필요한 서버 URL이라 blob 로더로 렌더한다.
      */}
      <PhotoLightbox
        photos={photos.map((p) => ({ url: p.url }))}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
        renderImage={(photo, style) =>
          photo.url.startsWith('blob:') ? (
            <img src={photo.url} alt="" style={style} draggable={false} />
          ) : (
            <ThumbImg url={photo.url} imgStyle={style} />
          )
        }
      />

      {/* 인디포토 사진첩 선택 */}
      <IndiPhotoPickerModal
        open={indiPhotoOpen}
        siteId={selected?.siteId ?? null}
        siteName={selected?.siteName}
        subscribed={indiPhotoSubscribed}
        room={Math.max(0, PHOTO_POLICY.maxCount - photos.length)}
        onClose={() => setIndiPhotoOpen(false)}
        onConfirm={addIndiPhotos}
      />

      {/* 새 시공반장 등록 — 작업자·참조인 공용(§2.14 동일 양식·동일 컴포넌트 재사용) */}
      {addPartnerFor && (
        <PartnerFormPopup onClose={() => setAddPartnerFor(null)} onSuccess={handlePartnerCreated} />
      )}

      {/* 공사기간 입력 — site만 있는 현장 선택 시(schedule-lookup 공사기간 불완전) */}
      {periodPopupSite != null && (
        <ConstructionPeriodModal
          siteId={periodPopupSite.siteId}
          siteName={periodPopupSite.siteName}
          onClose={() => setPeriodPopupSite(null)}
          onComplete={handlePeriodPopupComplete}
        />
      )}

      {/* 현장 변경 확인 */}
      <Modal open={pendingSite != null} onClose={() => setPendingSite(null)} title="현장을 변경하시겠습니까?">
        <p className="text-body-md leading-relaxed text-text-secondary">
          현장을 변경하면 작성 중인 내용과 첨부 사진, 수신자 선택이 초기화됩니다.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setPendingSite(null)}>
            취소
          </Button>
          <Button size="sm" onClick={confirmChangeSite}>
            변경하기
          </Button>
        </div>
      </Modal>

      {/* 알림톡 미리보기(REQ-1-11) — 등록화면 [카카오톡 발송]과 별개인 두번째 발송 경로. 모달 안 [발송하기]도 같은 send() 호출 */}
      {previewOpen && worker && (
        <Modal open onClose={() => setPreviewOpen(false)} title="카카오톡 미리보기" size="lg">
          <div data-group="02" className="mx-auto w-full max-w-[420px] overflow-hidden rounded-lg border border-border">
            <WorkOrderNoticeView
              data={{
                senderCompanyName: session?.compName ?? '',
                senderRepName: session?.memberName ?? '',
                requestedByLabel: buildRequestedByLabel(session?.memberName, session?.compName),
                recipientName: ccList.length ? `${worker.partnerName} 외 참조인 ${ccList.length}명` : worker.partnerName,
                requestDate: today.date.replace(/\./g, '-'),
                siteName: selected?.siteName ?? '',
                customerName: selected?.customerName ?? '',
                orderContent: content,
              }}
              photos={photos}
              renderThumb={(p) => <PhotoPreview photo={p} className="h-full w-full object-cover" />}
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setPreviewOpen(false)}>
              취소
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setPreviewOpen(false);
                void send();
              }}
            >
              발송하기
            </Button>
          </div>
        </Modal>
      )}

      {/* 발송 완료 */}
      <Modal open={result != null} onClose={() => setResult(null)} title="발송 완료">
        {/* 폭은 .modal-box(max-w 420) + .modal-body 좌우 패딩(18px)에 맡긴다 — 고정폭을 주면 여백이 한쪽으로 쏠린다. */}
        <div className="w-full">
          <p className="text-body-md leading-relaxed text-text-secondary">
            작업요청이{' '}
            <b className="font-bold text-text">{sentRecipientNames || '수신'}</b> 반장님께 알림톡으로 안내되었습니다.
          </p>
          {deliveredNoticeText(result?.deliveredCustReqCount) && (
            <p className="mt-02 text-body-md font-medium text-text">{deliveredNoticeText(result?.deliveredCustReqCount)}</p>
          )}
          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              onClick={() => {
                setResult(null);
                navigate(`${BASE}/workorder`);
              }}
            >
              목록으로 이동
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// 폼 행 레이아웃 (라벨 + 내용)
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-b border-border px-4 py-3">
      <span className="mt-1 w-[64px] shrink-0 text-body-sm font-medium text-text-secondary">{label}</span>
      {children}
    </div>
  );
}
